import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { DealsSidebarComponent } from '@modules/projects/list/deals-sidebar/deals-sidebar.component';
import { TsOverlayComponent, TsOverlayModule, TsPipesModule } from 'tsui';
import { TreoNavigationItem } from 'tsui/@treo';
import { ShellViewLoaderComponent } from '../shell-view-loader/shell-view-loader.component';
import { ShellTaskSidebarComponent } from '../shell-task-sidebar/shell-task-sidebar.component';
import { ShellReportSidebarComponent } from '../shell-report-sidebar/shell-report-sidebar.component';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { BehaviorSubject, debounceTime, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { iconMap } from '../functions';
import { PipesModule } from 'app/shared/pipes/pipes.module';

@Component({
  selector: 'shell-menu',
  standalone: true,
  imports: [
    TsPipesModule,
    RouterModule,
    TsOverlayModule,
    MatIconModule,
    DealsSidebarComponent,
    ShellViewLoaderComponent,
    ShellTaskSidebarComponent,
    ShellReportSidebarComponent,
    NgClass,
    LucideAngularModule,
    PipesModule,
  ],
  templateUrl: './shell-menu.component.html',
  styleUrl: './shell-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellMenuComponent implements OnInit {
  menuItems = input<TreoNavigationItem[]>([]);
  lastUrl = input<string>('');
  destroyRef = inject(DestroyRef);
  isOverlayOpenFor = signal<{ id: string; icon: string; urlPrefix: string } | undefined>(undefined);
  menuOverlayBox = viewChild<TsOverlayComponent>('menuOverlayBox');
  menuContainer = viewChild<ElementRef>('menuContainer');
  iconMap = iconMap;
  hover = new BehaviorSubject<any | undefined>(undefined);

  ngOnInit(): void {
    this.hover
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(250),
        filter((value) => this.isOverlayOpenFor()?.id !== value?.id),
      )
      .subscribe((value) => {
        this.menuOverlayBox().closeOverlay();

        if (value) {
          this.openMenu(value);
        } else {
          this.isOverlayOpenFor.set(undefined);
        }
      });
  }

  onNavigate(): void {
    this.isOverlayOpenFor.set(undefined);
    this.menuOverlayBox().closeOverlay();
  }

  isUrlMatches(urlMatches: string[]): boolean {
    let url = this as unknown as string;

    if (!url) {
      return false;
    }

    if (url.includes('/space')) {
      if (url.includes('?')) {
        url = url.split('?')[0];
      }

      return urlMatches?.some((m) => url.replace(/\/\d+/, '') === m);
    }

    return urlMatches?.some((m) => url.startsWith(m));
  }

  openMenu(item: { id: string; icon: string; urlPrefix: string; meta?: { urlPrefix: string } }): void {
    if (this.isOverlayOpenFor()?.id === item.id) {
      return;
    }
    this.isOverlayOpenFor.set({
      id: item.id,
      icon: item.icon,
      urlPrefix: item.meta?.urlPrefix ?? item.urlPrefix,
    });
    this.menuOverlayBox().displayOverlay(this.menuContainer().nativeElement);
  }
}
